import { Loan } from './loans'
import { DeductionEvent } from './deductions'
import { CallRecord } from './calls'

export interface OfficerData {
  name: string
  // Monthly scorecard
  monthlyPoints: number
  monthlyGoal: number
  creditPulls: number
  docOuts: number
  submissions: number
  closings: number
  speedBonusFastCloses: number
  speedBonusPoints: number
  callAnswerRate: number
  dials: number
  // Today scorecard
  todayTarget: number
  todayPoints: number
  todayCreditPulls: number
  todayDocOuts: number
  todaySubmissions: number
  todayClosings: number
  todayCallAnswerRate: number
  todayDials: number
  // Bonus eligibility
  unitsClosed: number
  // Career progression
  currentRank: string
  nextRank: string
  tenureProgress: string // e.g. "1yr 4mo / 2yr"
  tenurePercent: number
  unitsForNextRank: number
  unitsNeeded: number
  unitsPercent: number
  actionPlan: string
  // Data
  loans: Loan[]
  deductions: DeductionEvent[]
  calls: CallRecord[] // recent month-to-date call sample
  todayCalls: CallRecord[] // today's call sample
}

// Compact spec -> CallRecord helper. Each entry: [contact, phone, time, durationSec, answered, outcome]
type CallSpec = [string, string, string, number, boolean, string]
function makeCalls(
  prefix: string,
  date: string,
  specs: CallSpec[],
): CallRecord[] {
  return specs.map(
    ([contact, phone, time, durationSec, answered, outcome], i) => ({
      id: `${prefix}-CALL-${i + 1}`,
      contact,
      phone,
      date,
      time,
      durationSec,
      answered,
      outcome,
    }),
  )
}

// John Davis - ABOVE GOAL, bonus unlocked, 6 units
const johnDavisLoans: Loan[] = [
  {
    id: 'JD-001',
    borrower: 'Robert & Linda Martinez',
    property: '1245 Maple Ave, Austin, TX 78704',
    loanAmount: 425000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 1,
    speedBonus: 1500,
    events: [
      { stage: 'credit_pull', date: '2025-05-01', points: 750 },
      { stage: 'doc_out', date: '2025-05-03', points: 500 },
      { stage: 'submission', date: '2025-05-08', points: 500 },
      { stage: 'closing', date: '2025-05-13', points: 250 },
    ],
  },
  {
    id: 'JD-002',
    borrower: 'Jennifer Patel',
    property: '892 Oakwood Dr, Round Rock, TX 78664',
    loanAmount: 312000,
    loanType: 'FHA',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 4,
    speedBonus: 1500,
    events: [
      { stage: 'credit_pull', date: '2025-04-22', points: 750 },
      { stage: 'doc_out', date: '2025-04-28', points: 500 },
      { stage: 'submission', date: '2025-05-05', points: 500 },
      { stage: 'closing', date: '2025-05-10', points: 250 },
    ],
  },
  {
    id: 'JD-003',
    borrower: 'Marcus Thompson',
    property: '4501 Cedar Ln, Cedar Park, TX 78613',
    loanAmount: 580000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 7,
    speedBonus: 500,
    events: [
      { stage: 'credit_pull', date: '2025-04-15', points: 750 },
      { stage: 'doc_out', date: '2025-04-20', points: 500 },
      { stage: 'submission', date: '2025-04-30', points: 500 },
      { stage: 'closing', date: '2025-05-07', points: 250 },
    ],
  },
  {
    id: 'JD-004',
    borrower: 'Sophia Nguyen',
    property: '7820 Riverbend Rd, Austin, TX 78746',
    loanAmount: 695000,
    loanType: 'Jumbo',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 2,
    events: [
      { stage: 'credit_pull', date: '2025-05-02', points: 750 },
      { stage: 'doc_out', date: '2025-05-06', points: 500 },
      { stage: 'submission', date: '2025-05-11', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'JD-005',
    borrower: 'David & Amy Chen',
    property: '331 Hillcrest Way, Pflugerville, TX 78660',
    loanAmount: 385000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-04', points: 750 },
      { stage: 'doc_out', date: '2025-05-08', points: 500 },
      { stage: 'submission', date: '2025-05-12', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'JD-006',
    borrower: 'Carlos Reyes',
    property: '156 Sunset Blvd, Leander, TX 78641',
    loanAmount: 298000,
    loanType: 'VA',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-09', points: 750 },
      { stage: 'doc_out', date: '2025-05-11', points: 500 },
      { stage: 'submission', date: '2025-05-13', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'JD-007',
    borrower: 'Olivia Brooks',
    property: '2244 Greenwood St, Austin, TX 78702',
    loanAmount: 445000,
    loanType: 'Conventional',
    currentStage: 'doc_out',
    isMoving: true,
    daysInCurrentStage: 2,
    events: [
      { stage: 'credit_pull', date: '2025-05-12', points: 750 },
      { stage: 'doc_out', date: '2025-05-14', points: 500 },
    ],
  },
  {
    id: 'JD-008',
    borrower: 'James Whitfield',
    property: '6708 Lakeshore Dr, Austin, TX 78732',
    loanAmount: 825000,
    loanType: 'Jumbo',
    currentStage: 'credit_pull',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [{ stage: 'credit_pull', date: '2025-05-14', points: 750 }],
  },
]

const johnDavisDeductions: DeductionEvent[] = [
  {
    id: 'JD-DED-001',
    type: 'withdrawn',
    label: 'File Withdrawn',
    detail: 'Morgan — borrower withdrew application',
    date: '2025-05-10',
    points: -500,
  },
]

// Sarah Mitchell - MID-PACE, under goal, 3 units, bonus locked
const sarahMitchellLoans: Loan[] = [
  {
    id: 'SM-001',
    borrower: 'Alex Johnson',
    property: '789 Oak Street, Austin, TX 78701',
    loanAmount: 350000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 3,
    events: [
      { stage: 'credit_pull', date: '2025-05-03', points: 750 },
      { stage: 'doc_out', date: '2025-05-06', points: 500 },
      { stage: 'submission', date: '2025-05-10', points: 500 },
      { stage: 'closing', date: '2025-05-13', points: 250 },
    ],
  },
  {
    id: 'SM-002',
    borrower: 'Maria Garcia',
    property: '456 Pine Ave, Round Rock, TX 78664',
    loanAmount: 280000,
    loanType: 'FHA',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 1,
    events: [
      { stage: 'credit_pull', date: '2025-05-05', points: 750 },
      { stage: 'doc_out', date: '2025-05-08', points: 500 },
      { stage: 'submission', date: '2025-05-11', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'SM-003',
    borrower: 'Tom Wilson',
    property: '321 Elm Dr, Cedar Park, TX 78613',
    loanAmount: 420000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 5,
    speedBonus: 500,
    events: [
      { stage: 'credit_pull', date: '2025-04-28', points: 750 },
      { stage: 'doc_out', date: '2025-05-01', points: 500 },
      { stage: 'submission', date: '2025-05-06', points: 500 },
      { stage: 'closing', date: '2025-05-09', points: 250 },
    ],
  },
  {
    id: 'SM-004',
    borrower: 'Lisa Brown',
    property: '654 Maple Ct, Austin, TX 78746',
    loanAmount: 510000,
    loanType: 'Jumbo',
    currentStage: 'submission',
    isMoving: true,
    daysInCurrentStage: 1,
    events: [
      { stage: 'credit_pull', date: '2025-05-07', points: 750 },
      { stage: 'doc_out', date: '2025-05-10', points: 500 },
      { stage: 'submission', date: '2025-05-13', points: 500 },
    ],
  },
  {
    id: 'SM-005',
    borrower: 'Kevin Lee',
    property: '987 Birch Ln, Pflugerville, TX 78660',
    loanAmount: 395000,
    loanType: 'VA',
    currentStage: 'doc_out',
    isMoving: false,
    daysInCurrentStage: 4,
    events: [
      { stage: 'credit_pull', date: '2025-05-06', points: 750 },
      { stage: 'doc_out', date: '2025-05-10', points: 500 },
    ],
  },
]

const sarahMitchellDeductions: DeductionEvent[] = [
  {
    id: 'SM-DED-001',
    type: 'file_not_moving',
    label: 'File Not Moving',
    detail: 'Lee — Doc Out stuck 4 days',
    date: '2025-05-14',
    points: -50,
    relatedLoanId: 'SM-005',
  },
  {
    id: 'SM-DED-002',
    type: 'withdrawn',
    label: 'File Withdrawn',
    detail: 'Reyes — application withdrawn',
    date: '2025-05-09',
    points: -500,
  },
]

// Michael Chen - LOW, under goal, 1 unit, bonus locked, newer officer
const michaelChenLoans: Loan[] = [
  {
    id: 'MC-001',
    borrower: 'Patricia Davis',
    property: '123 River Rd, Austin, TX 78704',
    loanAmount: 315000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 2,
    events: [
      { stage: 'credit_pull', date: '2025-05-06', points: 750 },
      { stage: 'doc_out', date: '2025-05-09', points: 500 },
      { stage: 'submission', date: '2025-05-12', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'MC-002',
    borrower: 'Steven Martinez',
    property: '456 Lake View, Round Rock, TX 78664',
    loanAmount: 275000,
    loanType: 'FHA',
    currentStage: 'submission',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-10', points: 750 },
      { stage: 'doc_out', date: '2025-05-12', points: 500 },
      { stage: 'submission', date: '2025-05-14', points: 500 },
    ],
  },
  {
    id: 'MC-003',
    borrower: 'Nancy Rodriguez',
    property: '789 Hill St, Cedar Park, TX 78613',
    loanAmount: 385000,
    loanType: 'Conventional',
    currentStage: 'doc_out',
    isMoving: true,
    daysInCurrentStage: 1,
    events: [
      { stage: 'credit_pull', date: '2025-05-11', points: 750 },
      { stage: 'doc_out', date: '2025-05-13', points: 500 },
    ],
  },
  {
    id: 'MC-004',
    borrower: 'Daniel Kim',
    property: '321 Park Ave, Austin, TX 78702',
    loanAmount: 425000,
    loanType: 'Conventional',
    currentStage: 'credit_pull',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [{ stage: 'credit_pull', date: '2025-05-14', points: 750 }],
  },
]

const michaelChenDeductions: DeductionEvent[] = []

// Jessica Rodriguez - ABOVE GOAL, 8 units, Senior LO targeting Executive
const jessicaRodriguezLoans: Loan[] = [
  {
    id: 'JR-001',
    borrower: 'William Taylor',
    property: '111 First St, Austin, TX 78701',
    loanAmount: 450000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 1,
    speedBonus: 1500,
    events: [
      { stage: 'credit_pull', date: '2025-05-01', points: 750 },
      { stage: 'doc_out', date: '2025-05-03', points: 500 },
      { stage: 'submission', date: '2025-05-07', points: 500 },
      { stage: 'closing', date: '2025-05-13', points: 250 },
    ],
  },
  {
    id: 'JR-002',
    borrower: 'Emma Anderson',
    property: '222 Second Ave, Round Rock, TX 78664',
    loanAmount: 380000,
    loanType: 'FHA',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 2,
    speedBonus: 1500,
    events: [
      { stage: 'credit_pull', date: '2025-05-02', points: 750 },
      { stage: 'doc_out', date: '2025-05-04', points: 500 },
      { stage: 'submission', date: '2025-05-08', points: 500 },
      { stage: 'closing', date: '2025-05-12', points: 250 },
    ],
  },
  {
    id: 'JR-003',
    borrower: 'James White',
    property: '333 Third Blvd, Cedar Park, TX 78613',
    loanAmount: 520000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 3,
    speedBonus: 500,
    events: [
      { stage: 'credit_pull', date: '2025-04-25', points: 750 },
      { stage: 'doc_out', date: '2025-04-28', points: 500 },
      { stage: 'submission', date: '2025-05-03', points: 500 },
      { stage: 'closing', date: '2025-05-11', points: 250 },
    ],
  },
  {
    id: 'JR-004',
    borrower: 'Olivia Harris',
    property: '444 Fourth St, Austin, TX 78746',
    loanAmount: 625000,
    loanType: 'Jumbo',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-05', points: 750 },
      { stage: 'doc_out', date: '2025-05-08', points: 500 },
      { stage: 'submission', date: '2025-05-11', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'JR-005',
    borrower: 'Noah Martin',
    property: '555 Fifth Ave, Pflugerville, TX 78660',
    loanAmount: 410000,
    loanType: 'VA',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 1,
    events: [
      { stage: 'credit_pull', date: '2025-05-06', points: 750 },
      { stage: 'doc_out', date: '2025-05-09', points: 500 },
      { stage: 'submission', date: '2025-05-12', points: 500 },
      { stage: 'closing', date: '2025-05-13', points: 250 },
    ],
  },
  {
    id: 'JR-006',
    borrower: 'Ava Thompson',
    property: '666 Sixth Ln, Leander, TX 78641',
    loanAmount: 355000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-07', points: 750 },
      { stage: 'doc_out', date: '2025-05-10', points: 500 },
      { stage: 'submission', date: '2025-05-12', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'JR-007',
    borrower: 'Liam Garcia',
    property: '777 Seventh Dr, Austin, TX 78702',
    loanAmount: 495000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 2,
    speedBonus: 500,
    events: [
      { stage: 'credit_pull', date: '2025-04-30', points: 750 },
      { stage: 'doc_out', date: '2025-05-02', points: 500 },
      { stage: 'submission', date: '2025-05-07', points: 500 },
      { stage: 'closing', date: '2025-05-12', points: 250 },
    ],
  },
  {
    id: 'JR-008',
    borrower: 'Sophia Lee',
    property: '888 Eighth St, Round Rock, TX 78664',
    loanAmount: 540000,
    loanType: 'Jumbo',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-08', points: 750 },
      { stage: 'doc_out', date: '2025-05-10', points: 500 },
      { stage: 'submission', date: '2025-05-12', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'JR-009',
    borrower: 'Mason Clark',
    property: '999 Ninth Ave, Cedar Park, TX 78613',
    loanAmount: 375000,
    loanType: 'FHA',
    currentStage: 'submission',
    isMoving: true,
    daysInCurrentStage: 1,
    events: [
      { stage: 'credit_pull', date: '2025-05-09', points: 750 },
      { stage: 'doc_out', date: '2025-05-11', points: 500 },
      { stage: 'submission', date: '2025-05-13', points: 500 },
    ],
  },
  {
    id: 'JR-010',
    borrower: 'Isabella Lopez',
    property: '1010 Tenth Blvd, Austin, TX 78704',
    loanAmount: 460000,
    loanType: 'Conventional',
    currentStage: 'doc_out',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-12', points: 750 },
      { stage: 'doc_out', date: '2025-05-14', points: 500 },
    ],
  },
]

const jessicaRodriguezDeductions: DeductionEvent[] = []

// David Thompson - MID-PACE, under goal, 2 units, bonus locked
const davidThompsonLoans: Loan[] = [
  {
    id: 'DT-001',
    borrower: 'Christopher Moore',
    property: '100 Main St, Austin, TX 78701',
    loanAmount: 340000,
    loanType: 'Conventional',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 4,
    events: [
      { stage: 'credit_pull', date: '2025-05-04', points: 750 },
      { stage: 'doc_out', date: '2025-05-07', points: 500 },
      { stage: 'submission', date: '2025-05-10', points: 500 },
      { stage: 'closing', date: '2025-05-13', points: 250 },
    ],
  },
  {
    id: 'DT-002',
    borrower: 'Amanda Jackson',
    property: '200 Center Ave, Round Rock, TX 78664',
    loanAmount: 295000,
    loanType: 'FHA',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-08', points: 750 },
      { stage: 'doc_out', date: '2025-05-10', points: 500 },
      { stage: 'submission', date: '2025-05-12', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'DT-003',
    borrower: 'Ryan Miller',
    property: '300 Park Ln, Cedar Park, TX 78613',
    loanAmount: 425000,
    loanType: 'Conventional',
    currentStage: 'submission',
    isMoving: true,
    daysInCurrentStage: 2,
    events: [
      { stage: 'credit_pull', date: '2025-05-09', points: 750 },
      { stage: 'doc_out', date: '2025-05-11', points: 500 },
      { stage: 'submission', date: '2025-05-12', points: 500 },
    ],
  },
  {
    id: 'DT-004',
    borrower: 'Michelle Davis',
    property: '400 Oak Dr, Austin, TX 78746',
    loanAmount: 510000,
    loanType: 'Jumbo',
    currentStage: 'doc_out',
    isMoving: false,
    daysInCurrentStage: 5,
    events: [
      { stage: 'credit_pull', date: '2025-05-05', points: 750 },
      { stage: 'doc_out', date: '2025-05-09', points: 500 },
    ],
  },
]

const davidThompsonDeductions: DeductionEvent[] = [
  {
    id: 'DT-DED-001',
    type: 'file_not_moving',
    label: 'File Not Moving',
    detail: 'Davis — Doc Out stuck 5 days',
    date: '2025-05-14',
    points: -50,
    relatedLoanId: 'DT-004',
  },
]

// Emily Parker - LOW, under goal, 1 unit, bonus locked, Loan Partner
const emilyParkerLoans: Loan[] = [
  {
    id: 'EP-001',
    borrower: 'Brandon Wilson',
    property: '50 Valley Rd, Austin, TX 78704',
    loanAmount: 285000,
    loanType: 'FHA',
    currentStage: 'closing',
    isMoving: true,
    daysInCurrentStage: 1,
    events: [
      { stage: 'credit_pull', date: '2025-05-09', points: 750 },
      { stage: 'doc_out', date: '2025-05-11', points: 500 },
      { stage: 'submission', date: '2025-05-13', points: 500 },
      { stage: 'closing', date: '2025-05-14', points: 250 },
    ],
  },
  {
    id: 'EP-002',
    borrower: 'Ashley Martinez',
    property: '60 Creek St, Round Rock, TX 78664',
    loanAmount: 310000,
    loanType: 'Conventional',
    currentStage: 'submission',
    isMoving: true,
    daysInCurrentStage: 0,
    events: [
      { stage: 'credit_pull', date: '2025-05-11', points: 750 },
      { stage: 'doc_out', date: '2025-05-13', points: 500 },
      { stage: 'submission', date: '2025-05-14', points: 500 },
    ],
  },
  {
    id: 'EP-003',
    borrower: 'Justin Anderson',
    property: '70 Hill Ave, Cedar Park, TX 78613',
    loanAmount: 365000,
    loanType: 'Conventional',
    currentStage: 'doc_out',
    isMoving: true,
    daysInCurrentStage: 1,
    events: [
      { stage: 'credit_pull', date: '2025-05-12', points: 750 },
      { stage: 'doc_out', date: '2025-05-13', points: 500 },
    ],
  },
]

const emilyParkerDeductions: DeductionEvent[] = []

// --- Per-officer recent call samples (a readable slice of their dials) ---
const johnDavisCalls = makeCalls('JD', '2025-05-13', [
  ['Morgan Lee', '(512) 555-0142', '9:12 AM', 312, true, 'Discussed rate lock'],
  [
    'Priya Nair',
    '(512) 555-0188',
    '9:48 AM',
    0,
    false,
    'No answer — voicemail',
  ],
  ['Carlos Diaz', '(737) 555-0110', '10:21 AM', 540, true, 'Booked appt'],
  ['Dana White', '(512) 555-0173', '11:05 AM', 95, true, 'Quick follow-up'],
  ['Sam Porter', '(512) 555-0199', '1:30 PM', 0, false, 'No answer'],
  ['Grace Kim', '(737) 555-0144', '2:15 PM', 420, true, 'Sent doc checklist'],
  ['Owen Reyes', '(512) 555-0127', '3:02 PM', 210, true, 'Pre-qual review'],
  ['Tessa Cole', '(512) 555-0166', '4:18 PM', 0, false, 'Left voicemail'],
])
const johnDavisTodayCalls = makeCalls('JD-T', '2025-05-14', [
  ['Aaron Vance', '(512) 555-0101', '8:55 AM', 260, true, 'Rate question'],
  ['Lena Fox', '(737) 555-0133', '9:40 AM', 0, false, 'No answer'],
  ['Mike Stern', '(512) 555-0155', '10:30 AM', 480, true, 'Booked appt'],
  ['Rosa Lima', '(512) 555-0177', '11:50 AM', 140, true, 'Doc follow-up'],
])
const sarahMitchellCalls = makeCalls('SM', '2025-05-13', [
  ['Alex Johnson', '(512) 555-0210', '9:20 AM', 300, true, 'App walkthrough'],
  ['Brian Tate', '(512) 555-0233', '10:05 AM', 0, false, 'No answer'],
  ['Cindy Ross', '(737) 555-0241', '11:15 AM', 180, true, 'Rate update'],
  ['Derek Hall', '(512) 555-0255', '1:40 PM', 0, false, 'Left voicemail'],
  ['Ella Pace', '(512) 555-0277', '2:50 PM', 360, true, 'Doc checklist'],
])
const sarahMitchellTodayCalls = makeCalls('SM-T', '2025-05-14', [
  ['Faith Nole', '(512) 555-0288', '9:10 AM', 220, true, 'Follow-up'],
  ['Greg Yates', '(737) 555-0299', '10:25 AM', 0, false, 'No answer'],
  ['Holly Mince', '(512) 555-0301', '11:40 AM', 150, true, 'Quick check-in'],
])
const michaelChenCalls = makeCalls('MC', '2025-05-13', [
  ['Ian Brooks', '(512) 555-0410', '9:30 AM', 0, false, 'No answer'],
  ['Jade Lowe', '(512) 555-0433', '10:50 AM', 240, true, 'Pipeline update'],
  ['Kyle Senn', '(737) 555-0441', '1:10 PM', 0, false, 'Left voicemail'],
  ['Lori Park', '(512) 555-0455', '3:20 PM', 180, true, 'Rate review'],
])
const michaelChenTodayCalls = makeCalls('MC-T', '2025-05-14', [
  ['Nate Cobb', '(512) 555-0466', '9:35 AM', 0, false, 'No answer'],
  ['Opal Reed', '(737) 555-0477', '11:05 AM', 200, true, 'Doc follow-up'],
])
const jessicaRodriguezCalls = makeCalls('JR', '2025-05-13', [
  ['William Taylor', '(512) 555-0610', '8:45 AM', 420, true, 'Booked appt'],
  ['Xena Wood', '(512) 555-0633', '9:30 AM', 300, true, 'Rate lock'],
  ['Yusuf Adel', '(737) 555-0641', '10:15 AM', 0, false, 'No answer'],
  ['Zara Beck', '(512) 555-0655', '11:25 AM', 360, true, 'Doc checklist'],
  ['Adam Cruz', '(512) 555-0677', '1:35 PM', 540, true, 'Closing prep'],
  ['Bella Dunn', '(737) 555-0688', '2:45 PM', 280, true, 'Follow-up'],
])
const jessicaRodriguezTodayCalls = makeCalls('JR-T', '2025-05-14', [
  ['Cole Espino', '(512) 555-0701', '8:50 AM', 380, true, 'Booked appt'],
  ['Dina Frost', '(512) 555-0722', '9:55 AM', 260, true, 'Rate question'],
  ['Evan Gold', '(737) 555-0733', '11:10 AM', 320, true, 'Doc follow-up'],
])
const davidThompsonCalls = makeCalls('DT', '2025-05-13', [
  ['Faye Hunt', '(512) 555-0810', '9:25 AM', 200, true, 'Pre-qual'],
  ['Gabe Ives', '(512) 555-0833', '10:40 AM', 0, false, 'No answer'],
  ['Hana Jett', '(737) 555-0841', '1:20 PM', 0, false, 'Left voicemail'],
  ['Ivan Knox', '(512) 555-0855', '3:05 PM', 240, true, 'Rate review'],
])
const davidThompsonTodayCalls = makeCalls('DT-T', '2025-05-14', [
  ['Jana Lott', '(512) 555-0866', '9:45 AM', 180, true, 'Follow-up'],
  ['Kent Moss', '(737) 555-0877', '11:15 AM', 0, false, 'No answer'],
])
const emilyParkerCalls = makeCalls('EP', '2025-05-13', [
  ['Leah Nunn', '(512) 555-0910', '9:15 AM', 260, true, 'App walkthrough'],
  ['Milo Oaks', '(512) 555-0933', '10:30 AM', 0, false, 'No answer'],
  ['Nora Pugh', '(737) 555-0941', '11:45 AM', 200, true, 'Doc checklist'],
  ['Omar Quin', '(512) 555-0955', '2:10 PM', 0, false, 'Left voicemail'],
])
const emilyParkerTodayCalls = makeCalls('EP-T', '2025-05-14', [
  ['Pam Rhys', '(512) 555-0966', '9:50 AM', 220, true, 'Follow-up'],
  ['Quinn Sage', '(737) 555-0977', '11:25 AM', 0, false, 'No answer'],
])

export const OFFICERS: Record<string, OfficerData> = {
  'John Davis': {
    name: 'John Davis',
    monthlyPoints: 10250,
    monthlyGoal: 10000,
    creditPulls: 8,
    docOuts: 7,
    submissions: 6,
    closings: 6,
    speedBonusFastCloses: 3,
    speedBonusPoints: 2500,
    callAnswerRate: 96,
    dials: 450,
    todayTarget: 420,
    todayPoints: 2000,
    todayCreditPulls: 1,
    todayDocOuts: 1,
    todaySubmissions: 0,
    todayClosings: 3,
    todayCallAnswerRate: 95,
    todayDials: 22,
    unitsClosed: 6,
    currentRank: 'LOAN OFFICER',
    nextRank: 'SENIOR LOAN OFFICER',
    tenureProgress: '1yr 4mo / 2yr',
    tenurePercent: 66,
    unitsForNextRank: 11,
    unitsNeeded: 16,
    unitsPercent: 68,
    actionPlan:
      'Close 5 more units in the next 2 months to qualify for Senior Loan Officer via the units path.',
    loans: johnDavisLoans,
    deductions: johnDavisDeductions,
    calls: johnDavisCalls,
    todayCalls: johnDavisTodayCalls,
  },
  'Sarah Mitchell': {
    name: 'Sarah Mitchell',
    monthlyPoints: 7500,
    monthlyGoal: 10000,
    creditPulls: 5,
    docOuts: 4,
    submissions: 4,
    closings: 3,
    speedBonusFastCloses: 1,
    speedBonusPoints: 500,
    callAnswerRate: 88,
    dials: 320,
    todayTarget: 420,
    todayPoints: 1250,
    todayCreditPulls: 0,
    todayDocOuts: 0,
    todaySubmissions: 1,
    todayClosings: 1,
    todayCallAnswerRate: 85,
    todayDials: 15,
    unitsClosed: 3,
    currentRank: 'LOAN OFFICER',
    nextRank: 'SENIOR LOAN OFFICER',
    tenureProgress: '10mo / 2yr',
    tenurePercent: 42,
    unitsForNextRank: 8,
    unitsNeeded: 16,
    unitsPercent: 50,
    actionPlan:
      'Close 8 more units in the next 3 months to qualify for Senior Loan Officer via the units path.',
    loans: sarahMitchellLoans,
    deductions: sarahMitchellDeductions,
    calls: sarahMitchellCalls,
    todayCalls: sarahMitchellTodayCalls,
  },
  'Michael Chen': {
    name: 'Michael Chen',
    monthlyPoints: 4200,
    monthlyGoal: 10000,
    creditPulls: 4,
    docOuts: 2,
    submissions: 2,
    closings: 1,
    speedBonusFastCloses: 0,
    speedBonusPoints: 0,
    callAnswerRate: 78,
    dials: 210,
    todayTarget: 420,
    todayPoints: 1250,
    todayCreditPulls: 1,
    todayDocOuts: 0,
    todaySubmissions: 1,
    todayClosings: 1,
    todayCallAnswerRate: 75,
    todayDials: 8,
    unitsClosed: 1,
    currentRank: 'LOAN OFFICER',
    nextRank: 'SENIOR LOAN OFFICER',
    tenureProgress: '4mo / 2yr',
    tenurePercent: 17,
    unitsForNextRank: 3,
    unitsNeeded: 16,
    unitsPercent: 19,
    actionPlan:
      'Build your pipeline and increase conversions. Focus on consistent closings to reach Senior Loan Officer.',
    loans: michaelChenLoans,
    deductions: michaelChenDeductions,
    calls: michaelChenCalls,
    todayCalls: michaelChenTodayCalls,
  },
  'Jessica Rodriguez': {
    name: 'Jessica Rodriguez',
    monthlyPoints: 12750,
    monthlyGoal: 10000,
    creditPulls: 10,
    docOuts: 9,
    submissions: 9,
    closings: 8,
    speedBonusFastCloses: 4,
    speedBonusPoints: 3000,
    callAnswerRate: 98,
    dials: 520,
    todayTarget: 420,
    todayPoints: 2500,
    todayCreditPulls: 0,
    todayDocOuts: 1,
    todaySubmissions: 0,
    todayClosings: 3,
    todayCallAnswerRate: 100,
    todayDials: 28,
    unitsClosed: 8,
    currentRank: 'SENIOR LOAN OFFICER',
    nextRank: 'EXECUTIVE LOAN OFFICER',
    tenureProgress: '1yr 2mo / 1yr',
    tenurePercent: 100,
    unitsForNextRank: 18,
    unitsNeeded: 26,
    unitsPercent: 69,
    actionPlan:
      'Close 8 more units in the next 4 months to qualify for Executive Loan Officer via the units path.',
    loans: jessicaRodriguezLoans,
    deductions: jessicaRodriguezDeductions,
    calls: jessicaRodriguezCalls,
    todayCalls: jessicaRodriguezTodayCalls,
  },
  'David Thompson': {
    name: 'David Thompson',
    monthlyPoints: 5750,
    monthlyGoal: 10000,
    creditPulls: 4,
    docOuts: 3,
    submissions: 3,
    closings: 2,
    speedBonusFastCloses: 0,
    speedBonusPoints: 0,
    callAnswerRate: 85,
    dials: 280,
    todayTarget: 420,
    todayPoints: 250,
    todayCreditPulls: 0,
    todayDocOuts: 0,
    todaySubmissions: 0,
    todayClosings: 1,
    todayCallAnswerRate: 82,
    todayDials: 12,
    unitsClosed: 2,
    currentRank: 'LOAN OFFICER',
    nextRank: 'SENIOR LOAN OFFICER',
    tenureProgress: '1yr 1mo / 2yr',
    tenurePercent: 54,
    unitsForNextRank: 6,
    unitsNeeded: 16,
    unitsPercent: 38,
    actionPlan:
      'Close 10 more units in the next 3 months to qualify for Senior Loan Officer via the units path.',
    loans: davidThompsonLoans,
    deductions: davidThompsonDeductions,
    calls: davidThompsonCalls,
    todayCalls: davidThompsonTodayCalls,
  },
  'Emily Parker': {
    name: 'Emily Parker',
    monthlyPoints: 3250,
    monthlyGoal: 10000,
    creditPulls: 3,
    docOuts: 2,
    submissions: 2,
    closings: 1,
    speedBonusFastCloses: 0,
    speedBonusPoints: 0,
    callAnswerRate: 92,
    dials: 350,
    todayTarget: 420,
    todayPoints: 1250,
    todayCreditPulls: 0,
    todayDocOuts: 0,
    todaySubmissions: 1,
    todayClosings: 1,
    todayCallAnswerRate: 90,
    todayDials: 18,
    unitsClosed: 1,
    currentRank: 'LOAN PARTNER',
    nextRank: 'LOAN OFFICER',
    tenureProgress: '2mo / 6mo',
    tenurePercent: 33,
    unitsForNextRank: 0,
    unitsNeeded: 0,
    unitsPercent: 0,
    actionPlan:
      'Pass your SAFE exam and obtain required licenses to become a Loan Officer.',
    loans: emilyParkerLoans,
    deductions: emilyParkerDeductions,
    calls: emilyParkerCalls,
    todayCalls: emilyParkerTodayCalls,
  },
}

export function getOfficer(name: string): OfficerData {
  return OFFICERS[name] || OFFICERS['John Davis']
}
